/**
 * 全站 SEO 配置（唯一维护入口）
 *
 * 说明：
 * 1. 站点是 Vue SPA（history 模式），百度蜘蛛对 JS 渲染支持很弱，
 *    所以此前蜘蛛抓到的几乎是空页面 —— 快照摘要、缩略图全靠“猜”。
 * 2. 这份文件提供两层修复：
 *    - SITE / PAGE_SEO：给每个路由一份真实的 title/keywords/description；
 *    - setSeo()：在路由切换时同步写入 document.title 与 <meta> 标签。
 * 3. 代码层之外的两件事（无法靠改代码完成，见交付说明）：
 *    - 构建时预渲染（让蜘蛛抓到完整 HTML，收录的核心解法）；
 *    - 百度搜索资源平台提交 sitemap + 链接提交。
 */

// 站点主域：必须与线上实际访问域名一致（含协议，结尾不带斜杠）
//
// ✅ 已定稿（2026-10-14）：主域 = https://www.yuetaixing.com
//    线上只保留两个域名：www.yuetaixing.com（主）、yuetaixing.com（301 收口到主域）
//    两个 .cn 域名（yuetaixing.cn / www.yuetaixing.cn）已解绑，留给其他站点使用，
//    本项目的 canonical / sitemap / robots / nginx 里都不应再出现 .cn。
//
// ⚠️ 改域名时这 4 处必须一起改（缺一处就会产生重复内容或不一致）：
//    1) 本文件 SITE.url
//    2) public/index.html 的 canonical / alternate / og:url / og:image / JSON-LD url+logo
//    3) public/sitemap.xml 的全部 <loc>
//    4) public/robots.txt 首行注释与 Sitemap 行
//    另见 deploy/nginx-yuetaixing.conf 里的 301 收口配置。
export const SITE = {
  url: 'https://www.yuetaixing.com',
  name: '湖南岳泰兴医疗科技有限公司',
  shortName: '岳泰兴医疗',
  slogan: '用科技服务人类健康',
  logo: '/status/logoytx.png',
}

// 首页默认 TDK（也会作为 index.html 的兜底）
// 首页标题按“主推产品词 + 品牌词”的组合来写：主推产品是智慧导诊，所以产品词排在最前面
export const HOME_SEO = {
  title: '智慧导诊系统_医院自助机厂家_湖南岳泰兴医疗科技有限公司',
  keywords: '智慧导诊,智慧导诊系统,智慧导诊一体机,智慧导诊厂家,智慧导诊机器人,智慧导诊导航一体机,挂号缴费一体机,医院自助机,医院自助服务终端,智能导诊系统,院内导航系统,湖南智慧导诊厂家,岳泰兴医疗,湖南岳泰兴医疗科技有限公司',
  description:
    `${SITE.name}是湖南的智慧导诊系统与医院自助终端厂家，自主研发岳泰兴智慧导诊一体机、智慧导诊机器人、` +
    '挂号缴费一体机、医院自助机等产品，集智能分诊、科室信息查询、院内导航、健康宣教于一体，' +
    '帮助医院优化就医流程、缓解挂号难与找科室难，同时提供智慧医院、互联网医院、康复理疗设备等整体解决方案。',
}

// 各页面 TDK（key = 路由 name；手机端路由会自动回退到去掉 Phone 后缀的同名页面）
export const PAGE_SEO = {
  index: HOME_SEO,

  brandConcept: {
    title: `品牌理念_${SITE.shortName}`,
    keywords: '岳泰兴医疗,品牌理念,企业理念,用科技服务人类健康,大健康',
    description:
      `${SITE.name}品牌理念：${SITE.slogan}。聚焦大健康领域，以科技创新推动医疗健康服务升级，` +
      '让优质医疗资源触手可及。',
  },

  // 注意：键名必须与 src/router/index.js 里的 route name 一致（此处为 intelligentGuidance，不是路径名）
  intelligentGuidance: {
    title: '智慧导诊系统_智慧导诊一体机厂家_挂号缴费一体机_医院自助机',
    keywords: [
      // —— 主词
      '智慧导诊', '智慧导诊系统', '智慧导诊一体机', '智慧导诊厂家', '智慧导诊机器人', '智慧导诊机',
      // —— 相关设备词
      '挂号缴费一体机', '医院自助机', '医院自助服务终端', '自助挂号缴费机', '医院自助挂号机',
      '门诊自助终端', '医院导诊机', '导诊机器人',
      // —— 系统/方案词
      '智能导诊系统', '智能导诊机', '医院智能导诊', '院内导航系统', '医院导航系统', '门诊导诊系统',
      // —— 地域词（厂家类关键词竞争激烈，加地域更容易拿到排名）
      '湖南智慧导诊厂家', '长沙智慧导诊', '湖南医院自助机厂家',
      // —— 品牌词
      '岳泰兴智慧导诊', '岳泰兴医疗',
    ].join(','),
    description:
      '岳泰兴智慧导诊系统厂家：自主研发智慧导诊一体机、智慧导诊机器人、挂号缴费一体机、医院自助机等自助服务终端，' +
      '聚焦“优化就医流程”核心需求，通过人工智能与物联网技术连接患者与医院服务，' +
      '提供智能导诊、科室信息查询、院内导航、健康宣教四大核心功能，' +
      '支持触屏与语音多模态交互，24小时在线，帮助医院解决“挂号难、找科室难、信息查询难”等痛点，' +
      '减少导诊人员工作量、降低运营成本。',
  },

  coreProduct: {
    title: `核心产品_智慧导诊一体机_医院自助机_${SITE.shortName}`,
    keywords: '智慧导诊一体机,智慧导诊系统,医院自助机,挂号缴费一体机,智慧医院,智慧病房,智慧门诊,互联网医院,HIS系统,住院床旁交互,智慧心理,智慧中医,康复理疗设备',
    description:
      '岳泰兴医疗核心产品体系：以智慧导诊一体机、智慧导诊机器人、挂号缴费一体机、医院自助机为主推产品，' +
      '同时涵盖智慧医院、智慧病房、智慧门诊、互联网医院、HIS 系统、住院床旁交互、' +
      '智慧心理、智慧中医及智能化评估训练系统等医疗信息化与康养产品整体解决方案。',
  },

  enterpriseInformation: {
    title: `企业资讯_新闻动态_${SITE.shortName}`,
    keywords: '岳泰兴医疗,企业资讯,公司新闻,行业动态',
    description:
      `${SITE.name}最新动态：企业荣誉、产品发布、行业活动与招聘信息，了解岳泰兴医疗科技的发展足迹。`,
  },

  businessCooperation: {
    title: `商务合作_渠道代理_政企采购_${SITE.shortName}`,
    keywords: '商务合作,渠道代理,医院信息化采购,医疗设备代理商,岳泰兴医疗合作',
    description:
      `${SITE.name}商务合作：面向医院、渠道代理商与政企客户提供智慧医疗产品合作、` +
      '康复理疗设备供应与定制化解决方案，欢迎洽谈合作。',
  },

  talentRecruitment: {
    title: `人才招聘_${SITE.shortName}`,
    keywords: '岳泰兴医疗招聘,医疗科技招聘,电商运营招聘,销售经理招聘',
    description:
      `${SITE.name}人才招聘：电商运营、销售经理、医疗器械销售等岗位热招中，${SITE.slogan}，期待与你同行。`,
  },

  recruitInfo: {
    title: `招聘详情_${SITE.shortName}`,
    keywords: '岳泰兴医疗招聘,招聘岗位详情',
    description: `${SITE.name}在招岗位详情：岗位职责、任职要求与薪资待遇，欢迎投递简历加入岳泰兴医疗。`,
  },

  contactUs: {
    title: `联系我们_${SITE.shortName}`,
    keywords: '岳泰兴医疗联系方式,湖南岳泰兴医疗科技有限公司地址,合作咨询',
    description:
      `联系${SITE.name}：公司地址、联系电话与在线咨询入口，产品合作与售前售后问题欢迎随时联系我们。`,
  },

  // 详情类页面（列表点进去的），标题会由视图组件按实际内容动态覆盖
  coreProductInfo: {
    title: `产品详情_${SITE.shortName}`,
    keywords: '岳泰兴医疗产品详情,智慧医疗产品',
    description: `${SITE.shortName}产品详情：功能特性、应用场景与解决方案介绍。`,
  },
  enterpriseInformationInfo: {
    title: `资讯详情_${SITE.shortName}`,
    keywords: '岳泰兴医疗资讯详情,公司动态',
    description: `${SITE.shortName}企业资讯详情。`,
  },
}

/**
 * PC ↔ 手机端 URL 对照表
 *
 * 本站是「跳转适配」（PC 与手机端两套网址），按百度搜索资源平台的规范需要双向标注：
 *   - PC 页：<link rel="alternate" media="only screen and (max-width: 640px)" href="手机版URL">
 *   - 手机页：<link rel="canonical" href="PC版URL">
 * 两边都不标的话，百度会把手机端页面当成「与 PC 页重复的内容」，很难被单独收录。
 *
 * key = PC 端路由 name（手机端路由 name = PC name + “Phone”，手机首页为 phone）
 */
export const PC_PATH = {
  index: '/',
  brandConcept: '/brandConcept',
  intelligentGuidance: '/intelligentGuidanceIndex',
  enterpriseInformation: '/enterpriseInformation',
  businessCooperation: '/businessCooperation',
  coreProduct: '/coreProduct',
  talentRecruitment: '/talentRecruitment',
  contactUs: '/contactUs',
  coreProductInfo: '/coreProductInfo',
  enterpriseInformationInfo: '/enterpriseInformationInfo',
  recruitInfo: '/recruitInfo',
}

export const MOBILE_PATH = {
  index: '/phone',
  brandConcept: '/brandConceptPhone',
  intelligentGuidance: '/intelligentGuidanceIndexPhone',
  enterpriseInformation: '/enterpriseInformationPhone',
  businessCooperation: '/businessCooperationPhone',
  coreProduct: '/coreProductPhone',
  talentRecruitment: '/talentRecruitmentPhone',
  contactUs: '/contactUsPhone',
  coreProductInfo: '/coreProductInfoPhone',
  enterpriseInformationInfo: '/enterpriseInformationInfoPhone',
  recruitInfo: '/recruitInfoPhone',
}

/** 路由 name -> PC 端路由 name（手机端 xxxPhone -> xxx，phone -> index） */
export function toPcName (name) {
  const n = name || 'index'
  return n === 'phone' ? 'index' : n.replace(/Phone$/, '')
}

/** 是否手机端路由 */
export function isMobileName (name) {
  const n = name || ''
  return n === 'phone' || /Phone$/.test(n)
}

/**
 * 写入页面 TDK
 * @param {string} title
 * @param {string} [keywords]
 * @param {string} [description]
 * @param {string} [path] 当前路径，canonical 兜底用
 * @param {string} [canonicalPath] canonical 指向的路径（手机端传 PC 路径）
 * @param {string} [alternatePath] PC 端页面传手机版路径；手机端页面不传（会自动移除该标记）
 */
export function setSeo ({ title, keywords, description, path = '/', canonicalPath, alternatePath }) {
  if (typeof document === 'undefined') return

  document.title = title

  const upsert = (selector, attr, name, content) => {
    let el = document.head.querySelector(selector)
    if (!el) {
      el = document.createElement(attr === 'rel' ? 'link' : 'meta')
      el.setAttribute(attr, name)
      document.head.appendChild(el)
    }
    el.setAttribute('content', content)
  }

  if (keywords) upsert('meta[name="keywords"]', 'name', 'keywords', keywords)
  if (description) upsert('meta[name="description"]', 'name', 'description', description)

  // canonical：帮助百度把 /xxx?query 收敛到唯一规范地址，避免重复收录
  // 手机端页面则按跳转适配规范指向对应的 PC 页
  let canonical = document.head.querySelector('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', SITE.url + (canonicalPath || path))

  // 移动适配标注：只在 PC 页保留，手机端页面必须移除（否则会互相指认）
  let alternate = document.head.querySelector('link[rel="alternate"][data-seo-mobile]')
  if (alternatePath) {
    if (!alternate) {
      alternate = document.createElement('link')
      alternate.setAttribute('rel', 'alternate')
      alternate.setAttribute('data-seo-mobile', '1')
      document.head.appendChild(alternate)
    }
    alternate.setAttribute('media', 'only screen and (max-width: 640px)')
    alternate.setAttribute('href', SITE.url + alternatePath)
  } else if (alternate) {
    alternate.parentNode.removeChild(alternate)
  }
}

/**
 * 详情页 TDK：按 ?id= 从列表数据里取真实标题
 *
 * 为什么放在这里而不是各详情组件里：
 * router.afterEach 会在组件挂载后再写一次 TDK，组件里写在 created 的标题会被覆盖掉
 * （此前的实现就是这样失效的——详情页标题一直退化成“产品详情_岳泰兴医疗”）。
 * 统一由路由守卫按数据生成，PC 端与手机端一次性都对。
 */
const stripHtml = (s) =>
  String(s || '').replace(/<[^>]+>|&nbsp;/g, '').replace(/\s+/g, ' ').trim()
const clip = (s, n) => {
  const t = stripHtml(s)
  return t.length > n ? t.slice(0, n) : t
}

/** 列表数据按需加载（mock.js 有 50KB+，不能进主包） */
let MOCK = null
let FLAT_PRODUCTS = null
async function loadMock () {
  if (!MOCK) MOCK = await import('@/views/mock')
  return MOCK
}

const DETAIL_NAMES = ['coreProductInfo', 'enterpriseInformationInfo', 'recruitInfo']

async function resolveDetailSeo (pcName, route) {
  const mock = await loadMock()
  const id = route.query && route.query.id

  if (pcName === 'coreProductInfo') {
    if (!FLAT_PRODUCTS) FLAT_PRODUCTS = mock.coreProductList.flat()
    const item = FLAT_PRODUCTS.find((i) => String(i.id) === String(id))
    if (!item) return null
    return {
      title: `${item.name}_产品详情_${SITE.shortName}`,
      keywords: `${item.name},${SITE.shortName},智慧医疗产品,医院信息化系统,整体解决方案`,
      description: clip(item.info, 150) || PAGE_SEO.coreProductInfo.description,
    }
  }

  if (pcName === 'enterpriseInformationInfo') {
    const item = mock.enterpriseList.find((i) => String(i.id) === String(id))
    if (!item) return null
    return {
      title: `${stripHtml(item.text)}_${SITE.shortName}`,
      keywords: `${clip(item.text, 30)},${SITE.shortName},企业资讯,公司动态,医疗科技新闻`,
      description: clip(item.info, 150) || PAGE_SEO.enterpriseInformationInfo.description,
    }
  }

  if (pcName === 'recruitInfo') {
    const item = (mock.talentList || []).find((i) => String(i.id) === String(id))
    if (!item) return null
    return {
      title: `${item.name}_招聘_${SITE.shortName}`,
      keywords: `${item.name},${SITE.shortName},招聘,${item.address || '湖南'},医疗科技招聘`,
      description:
        `${SITE.name}在招岗位「${item.name}」：工作地点${item.address || '湖南'}，` +
        `招聘${item.num || ''}人。岗位职责、任职要求与薪资待遇详见页面，欢迎投递简历。`,
    }
  }

  return null
}

/**
 * 路由 -> TDK 解析（异步：详情页需要按需取列表数据）
 * 手机端路由（/phone、xxxPhone）自动复用 PC 端同名页面的 TDK
 */
export async function resolveSeo (route) {
  const name = route.name || 'index'
  const pcName = toPcName(name)

  // 详情页优先走动态 TDK（按 ?id= 取真实标题，取不到再回落静态配置）
  if (DETAIL_NAMES.indexOf(pcName) !== -1) {
    try {
      const hit = await resolveDetailSeo(pcName, route)
      if (hit) return hit
    } catch (e) {
      console.warn('[seo] 详情页 TDK 生成失败，回落到静态配置：', e && e.message)
    }
  }

  const hit = PAGE_SEO[pcName]
  if (!hit) {
    // 防呆：键名写错会静默回落到首页 TDK，导致该页标题变成首页标题，这里显式报出来
    console.warn(
      `[seo] 路由 “${name}” 没有对应的 PAGE_SEO 配置（键名应为 "${pcName}"），已回落到首页 TDK。` +
      '请检查 src/utils/seo.js 的键名是否与 router 中的 route name 一致。'
    )
  }
  return hit || PAGE_SEO.index
}
